import { create, type StoreApi, type UseBoundStore } from 'zustand';

/**
 * Minimal in-memory stack router (docs/01 §5 "minimal own router (tv)"). No URL sync: the
 * WebView never shows a URL and Back is a remote key, not browser history.
 */
export type ScreenName =
  'pairing' | 'sessionEnded' | 'home' | 'search' | 'profile' | 'playlist' | 'player';

export interface ScreenParams {
  pairing: undefined;
  /** TV-11: shown after `meta.status='closed'` or a `session_expired` API answer. */
  sessionEnded: undefined;
  home: undefined;
  search: { q?: string } | undefined;
  profile: undefined;
  /** Playlist view (docs/04 §3); `kind: 'artist'` reads `GET /artists/:id`. */
  playlist: { id: string; kind: 'playlist' | 'artist' };
  /** `drawer: true` opens the Player with the queue drawer open (the rail's "Fila" item). */
  player: { itemId?: string; songId?: string; tone?: string; drawer?: boolean } | undefined;
}

export interface RouteEntry<S extends ScreenName = ScreenName> {
  /** Unique per push; use as React key so re-pushing the same screen remounts it. */
  key: number;
  screen: S;
  params: ScreenParams[S];
}

/**
 * Rail items (docs/04 §7.6). "Fila" is not a screen any more: it opens the Player with the
 * queue drawer, so the list is keys, not screen names.
 */
export type RailItemKey = 'home' | 'search' | 'profile' | 'queue';
export const RAIL_ITEMS: readonly RailItemKey[] = ['home', 'search', 'profile', 'queue'];
export const ROOT_SCREEN: ScreenName = 'home';
/** Screens reachable while signed out (the shell's auth gate must not bounce them to Pairing). */
export const SIGNED_OUT_SCREENS: readonly ScreenName[] = ['pairing', 'sessionEnded'];

export interface RouterState {
  stack: RouteEntry[];
  navigate<S extends ScreenName>(screen: S, params?: ScreenParams[S]): void;
  replace<S extends ScreenName>(screen: S, params?: ScreenParams[S]): void;
  /** Resets the stack to a single entry (rail selection, sign-out → pairing). */
  reset<S extends ScreenName>(screen: S, params?: ScreenParams[S]): void;
  /** Pops one entry; returns false when already at the root (caller shows the exit dialog). */
  back(): boolean;
}

export type RouterApi = UseBoundStore<StoreApi<RouterState>>;

let keySeq = 0;
function entry<S extends ScreenName>(screen: S, params: ScreenParams[S]): RouteEntry<S> {
  keySeq += 1;
  return { key: keySeq, screen, params };
}

export function createRouter(initial: ScreenName = 'pairing'): RouterApi {
  return create<RouterState>()((set, get) => ({
    stack: [entry(initial, undefined)],
    navigate: (screen, params) =>
      set((s) => ({ stack: [...s.stack, entry(screen, params as ScreenParams[typeof screen])] })),
    replace: (screen, params) =>
      set((s) => ({
        stack: [...s.stack.slice(0, -1), entry(screen, params as ScreenParams[typeof screen])],
      })),
    reset: (screen, params) =>
      set({ stack: [entry(screen, params as ScreenParams[typeof screen])] }),
    back: () => {
      if (get().stack.length <= 1) return false;
      set((s) => ({ stack: s.stack.slice(0, -1) }));
      return true;
    },
  }));
}

export const useRouter: RouterApi = createRouter();

export const selectCurrent = (s: RouterState): RouteEntry => s.stack[s.stack.length - 1]!;
export const selectDepth = (s: RouterState): number => s.stack.length;
